import React, { useState, useRef } from 'react';
import { Camera, X, Check, Upload, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import VehicleOverlay from './VehicleOverlay';

const PHOTO_SLOTS = [
  { id: 'vorne_links', label: 'Vorne links', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/0l0d8093_1.png' },
  { id: 'front', label: 'Front', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/vizwwxiy_2.png' },
  { id: 'vorne_rechts', label: 'Vorne rechts', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/ftjo4cug_3.png' },
  { id: 'rechts', label: 'Rechts', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/72z687hd_4.png' },
  { id: 'hinten_links', label: 'Hinten links', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/5fkewfhr_5.png' },
  { id: 'heck', label: 'Heck', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/gc51cf5f_6.png' },
  { id: 'hinten_rechts', label: 'Hinten rechts', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/iotkpkw4_7.png' },
  { id: 'links', label: 'Links', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/0sydpndg_8.png' },
  { id: 'schaden1', label: 'Schaden 1', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/pz5thtfr_9.png' },
  { id: 'schaden2', label: 'Schaden 2', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/g08vdu9f_10.png' },
  { id: 'detail1', label: 'Detail 1', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/7i5s2bhq_11.png' },
  { id: 'detail2', label: 'Detail 2', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/1jnih7oq_12.png' },
  { id: 'tacho', label: 'Tacho', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/9aqjxe8t_13.png' },
  { id: 'vin', label: 'VIN', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/7vu145z5_14.png' },
  { id: 'fahrzeugschein', label: 'Fahrzeugschein', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/4822exx9_15.png' },
  { id: 'felge', label: 'Felge', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/q09xz76y_16.png' },
  { id: 'kennzeichen', label: 'Kennzeichen', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/4t6ls4f0_17.png' },
  { id: 'unfallbericht', label: 'Unfallbericht', example: 'https://customer-assets.emergentagent.com/job_e31ddb5c-5cce-4011-aa37-b301b94ff50a/artifacts/v2kn8apc_18.png' }
];

export { PHOTO_SLOTS };

export default function GuidedPhotoCapture({ photos, onPhotoUpload, onPhotoRemove, onClose }) {
  const [localPhotos, setLocalPhotos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const totalSlots = PHOTO_SLOTS.length;
  const allCount = photos.length + localPhotos.length;

  const handleCapture = () => fileInputRef.current?.click();

  // Find next slot to capture
  const usedIds = [...photos.map(p => p.type), ...localPhotos.map(p => p.type)];
  const nextSlot = PHOTO_SLOTS.find(s => !usedIds.includes(s.id));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const preview = URL.createObjectURL(file);
      const usedIds = [...photos.map(p => p.type), ...localPhotos.map(p => p.type)];
      const nextSlot = PHOTO_SLOTS.find(s => !usedIds.includes(s.id));
      if (nextSlot) {
        setLocalPhotos(prev => [...prev, { type: nextSlot.id, file, preview }]);
      }
    });
    e.target.value = '';
  };

  const removeLocal = (index) => {
    setLocalPhotos(prev => {
      const removed = prev[index];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUploadAll = () => {
    setIsUploading(true);
    localPhotos.forEach(p => onPhotoUpload(p.file, p.type));
    setLocalPhotos([]);
    setIsUploading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 flex flex-col" data-testid="guided-photo-capture">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/50 text-white flex-shrink-0">
        <button onClick={onClose} className="flex items-center gap-2 text-sm hover:text-gray-300">
          <X className="w-5 h-5" />
          <span className="hidden sm:inline">Schliessen</span>
        </button>
        <span className="text-sm font-medium">{allCount} / {totalSlots} Fotos</span>
        {localPhotos.length > 0 ? (
          <Button onClick={handleUploadAll} disabled={isUploading} className="bg-green-500 hover:bg-green-600 text-white text-sm h-8 px-4 gap-2">
            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Alle hochladen ({localPhotos.length})
          </Button>
        ) : (
          <button onClick={onClose} className="text-sm font-medium text-[#29beff] hover:text-white">Fertig</button>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-800 flex-shrink-0">
        <div className="h-full bg-[#29beff] transition-all duration-300" style={{ width: `${(allCount / totalSlots) * 100}%` }} />
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col relative">

        {/* Overlay frame - fullscreen guide */}
        <VehicleOverlay />

        {/* Aufgenommene Fotos - oben scrollbar */}
        {(photos.length > 0 || localPhotos.length > 0) && (
          <div className="px-4 pt-4 pb-2 relative z-10">
            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Aufgenommene Fotos</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {photos.map((photo) => {
                const slot = PHOTO_SLOTS.find(s => s.id === photo.type);
                return (
                  <div key={photo.type} className="relative group flex-shrink-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded overflow-hidden border-2 border-green-500">
                      <img src={photo.preview} alt={slot?.label} className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute top-0.5 right-0.5 bg-green-500 rounded-full p-0.5">
                      <Check className="w-2.5 h-2.5 text-white" />
                    </div>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
                      <button onClick={() => onPhotoRemove(photo.type)} className="p-1 bg-red-500 rounded-full">
                        <Trash2 className="w-3 h-3 text-white" />
                      </button>
                    </div>
                    <p className="text-[8px] text-green-400 text-center mt-0.5 truncate w-16 sm:w-20">{slot?.label}</p>
                  </div>
                );
              })}
              {localPhotos.map((photo, index) => {
                const slot = PHOTO_SLOTS.find(s => s.id === photo.type);
                return (
                  <div key={`new-${index}`} className="relative group flex-shrink-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded overflow-hidden border-2 border-[#29beff]">
                      <img src={photo.preview} alt={slot?.label} className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute top-0.5 right-0.5 bg-[#29beff] rounded-full p-0.5">
                      <Camera className="w-2.5 h-2.5 text-white" />
                    </div>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
                      <button onClick={() => removeLocal(index)} className="p-1 bg-red-500 rounded-full">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                    <p className="text-[8px] text-[#29beff] text-center mt-0.5 truncate w-16 sm:w-20">{slot?.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Center: next photo label */}
        <div className="flex-1 flex items-center justify-center relative z-10">
          {nextSlot ? (
            <p className="text-sm text-white/60">Nächstes Foto: <span className="text-[#29beff] font-medium">{nextSlot.label}</span></p>
          ) : (
            <p className="text-green-400 text-sm font-medium">Alle Fotos aufgenommen!</p>
          )}
        </div>

        {/* Bottom: camera button + examples */}
        <div className="relative z-10">
          {/* Camera button */}
          <div className="flex justify-center pb-3">
            <button
              onClick={handleCapture}
              className="w-18 h-18 p-5 rounded-full bg-white/90 hover:bg-white text-gray-900 flex items-center justify-center shadow-xl transition-transform active:scale-90"
            >
              <Camera className="w-8 h-8" />
            </button>
          </div>

          {/* Beispielfotos */}
          <div className="px-4 pb-4 bg-black/40 backdrop-blur-sm">
            <p className="text-[10px] text-gray-400 mb-1.5 uppercase tracking-wide">Beispielfotos</p>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {PHOTO_SLOTS.map((slot) => {
                const taken = photos.find(p => p.type === slot.id) || localPhotos.find(p => p.type === slot.id);
                return (
                  <div key={slot.id} className="flex-shrink-0">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded overflow-hidden border ${taken ? 'border-green-500' : 'border-gray-700'} relative`}>
                      <img src={slot.example} alt={slot.label} className={`w-full h-full object-cover ${taken ? 'opacity-30' : 'opacity-80'}`} />
                      {taken && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-green-400" />
                        </div>
                      )}
                    </div>
                    <p className="text-[7px] text-gray-500 text-center mt-0.5 truncate w-12 sm:w-14">{slot.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom upload bar */}
      {localPhotos.length > 0 && (
        <div className="px-4 py-3 bg-black/60 border-t border-gray-700 flex-shrink-0">
          <Button onClick={handleUploadAll} disabled={isUploading} className="w-full h-12 bg-green-500 hover:bg-green-600 text-white text-base gap-2">
            {isUploading ? <><Loader2 className="w-5 h-5 animate-spin" /> Wird hochgeladen...</> : <><Upload className="w-5 h-5" /> Alle {localPhotos.length} Fotos hochladen</>}
          </Button>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleFileChange} />
    </div>
  );
}
